import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, Building2, Quote } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY } from "../constants/company";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage() {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/public/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const roster = team.filter((m) => m.role !== "admin");

  return (
    <div className="min-h-screen bg-background" data-testid="public-team-page">
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="team-logo-link" className="flex items-center">
            <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-11 w-auto rounded-sm" />
          </Link>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/" data-testid="team-back-link"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/auth" data-testid="team-login-button">Client Login</Link>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative" data-testid="team-hero">
        <div className="absolute inset-0">
          <img src="/presentation.png" alt="The Laronta Pro Tax Services team" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <p className="text-xs tracking-[0.25em] uppercase text-secondary font-medium animate-fade-up">Taylorsville, Utah</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tighter text-white max-w-2xl leading-[1.05] animate-fade-up" style={{ animationDelay: "80ms" }}>
            The people behind your strategy.
          </h1>
          <p className="mt-5 text-lg text-white/85 max-w-xl animate-fade-up" style={{ animationDelay: "160ms" }}>
            Accurate. Reliable. Professional. Meet the team that treats your taxes like their own.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="ceo-spotlight">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5 rounded-md overflow-hidden border border-border">
            <img src="/aaron-makasi.png" alt="Aaron Makasi, CEO and Tax Professional" className="w-full h-full object-cover" data-testid="ceo-photo" />
          </div>
          <div className="md:col-span-7">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Leadership</p>
            <h2 className="mt-3 text-3xl lg:text-4xl font-heading font-semibold tracking-tight" data-testid="ceo-name">Aaron Makasi</h2>
            <p className="text-sm text-primary font-medium mt-1">CEO / Tax Professional</p>
            <div className="mt-6 relative">
              <Quote className="w-6 h-6 text-secondary absolute -top-1 -left-1" />
              <p className="pl-8 text-muted-foreground leading-relaxed">
                Aaron founded Laronta Pro Tax Services on a simple belief: every client — individual or business —
                deserves a strategy, not just a form. Under his leadership, the firm has grown into Taylorsville's
                trusted practice for corporate tax strategy, multi-state returns, and year-round planning.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button asChild>
                <Link to="/auth" data-testid="ceo-book-button">Book with Our Team <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline">
                <a href={COMPANY.phoneHref} data-testid="ceo-call-button"><Phone className="w-4 h-4 mr-2" /> {COMPANY.phone}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {roster.length > 0 && (
        <section className="bg-muted/50 border-y border-border" data-testid="roster-section">
          <div className="max-w-7xl mx-auto px-6 py-24">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Our Team</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
              Advisors, preparers, and problem solvers.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="roster-grid">
              {roster.map((m, i) => (
                <div key={m.name + i} className="bg-card border border-border rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-md transition-transform duration-200" data-testid={`roster-card-${i}`}>
                  {m.photo_url ? (
                    <img src={`${BACKEND}${m.photo_url}`} alt={m.name} className="w-full h-56 object-cover" />
                  ) : (
                    <div className="w-full h-56 bg-primary/10 flex items-center justify-center">
                      <span className="font-heading text-6xl font-semibold text-primary/40">{m.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-heading text-2xl font-semibold">{m.name}</h3>
                    {m.title && <p className="text-sm text-primary font-medium mt-0.5">{m.title}</p>}
                    {m.office && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-2">
                        <Building2 className="w-3.5 h-3.5" /> {m.office}{m.state ? ` · ${m.state}` : ""}
                      </p>
                    )}
                    {m.bio && <p className="text-sm text-muted-foreground leading-relaxed mt-3">{m.bio}</p>}
                    <Badge variant="secondary" className="mt-4 capitalize">{m.role}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="team-culture-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground font-medium">Our Office</p>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-semibold tracking-tight">
              A local team you can actually sit down with.
            </h2>
            <p className="mt-4 text-muted-foreground max-w-lg leading-relaxed">
              No call centers, no ticket queues. When you book an appointment, you meet a real advisor at our
              Redwood Road office who has already reviewed your file. Your success is our priority.
            </p>
            <div className="mt-6 text-sm text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">{COMPANY.name}</p>
              <p>{COMPANY.fullAddress}</p>
              <p>{COMPANY.phone} · {COMPANY.email}</p>
            </div>
          </div>
          <div className="md:col-span-6 rounded-md overflow-hidden border border-border">
            <img src="/team-office.png" alt="Laronta Pro Tax Services team at the office" className="w-full h-full object-cover min-h-[280px]" />
          </div>
        </div>
      </section>

      <footer className="border-t border-border" data-testid="team-footer">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between gap-6 text-sm text-muted-foreground">
          <img src="/logo.png" alt="Laronta Pro Tax Services" className="h-9 w-auto rounded-sm self-start" />
          <p>{COMPANY.fullAddress} · {COMPANY.phone}</p>
          <div className="flex gap-6">
            <Link to="/services" data-testid="team-footer-services-link" className="hover:text-foreground transition-colors">Services</Link>
            <Link to="/support" data-testid="team-footer-support-link" className="hover:text-foreground transition-colors">Support</Link>
            <Link to="/privacy" data-testid="team-footer-privacy-link" className="hover:text-foreground transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
